import { Check, CircleAlert, ExternalLink, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from 'shadcn/components/ui/Badge';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

/**
 * Table displaying configurations that can receive applied changes.
 * Shows current config first, then matching configs with selection checkboxes.
 *
 * @param {Array} configsWithMatches - Configs that have matching slices
 * @param {Object} currentConfig - The current config being edited
 * @param {boolean} disabled - Whether selection is disabled
 * @param {(config: Object) => Array} getMatchingSlices - Function to get matching slices for a config
 * @param {() => void} onToggleAll - Callback to toggle all configs
 * @param {(configId: string) => void} onToggleConfig - Callback to toggle config selection
 * @param {Object} saveStatuses - Map of config ID -> save status
 * @param {Set<string>} selectedConfigs - Set of selected config IDs
 * @returns {JSX.Element}
 */
function ConfigurationsTable({
    configsWithMatches,
    currentConfig,
    disabled,
    getMatchingSlices,
    onToggleAll,
    onToggleConfig,
    saveStatuses,
    selectedConfigs,
}) {
    const allSelected = configsWithMatches.length > 0 && configsWithMatches.every((c) => selectedConfigs.has(c.id));

    return (
        <div className="flex-1 min-w-0 border rounded-lg overflow-hidden flex flex-col">
            <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                <TableHeader className="bg-muted/50">
                    <TableRow>
                        <TableHead className="w-10">
                            <Checkbox
                                checked={allSelected}
                                onCheckedChange={onToggleAll}
                                disabled={disabled || configsWithMatches.length === 0}
                            />
                        </TableHead>
                        <TableHead>Configuration</TableHead>
                        <TableHead className="w-24 text-center">Published</TableHead>
                        <TableHead className="w-20 text-center">Slices</TableHead>
                        <TableHead className="w-16 text-center" />
                    </TableRow>
                </TableHeader>
            </Table>
            <div className="flex-1 overflow-y-auto">
                <Table className="table-fixed [&_td]:py-2 [&_th]:py-2">
                    <TableBody>
                        {/* Current config - always shown first */}
                        <CurrentConfigRow config={currentConfig} status={saveStatuses[currentConfig.id]} />

                        {configsWithMatches.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                    No other configurations found with matching slice names.
                                </TableCell>
                            </TableRow>
                        )}

                        {configsWithMatches.map((config) => (
                            <ConfigRow
                                key={config.id}
                                config={config}
                                isSelected={selectedConfigs.has(config.id)}
                                onToggle={() => onToggleConfig(config.id)}
                                status={saveStatuses[config.id]}
                                matchingSlices={getMatchingSlices(config)}
                                disabled={disabled}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

/**
 * Row displaying the current config (always selected, not toggleable)
 *
 * @param {Object} config - The current config being edited
 * @param {string} status - The status of the current config
 */
function CurrentConfigRow({ config, status }) {
    return (
        <TableRow className={cn(status === 'success' && 'bg-green-50', status === 'error' && 'bg-red-50')}>
            <TableCell className="w-10">
                <Checkbox checked disabled />
            </TableCell>
            <TableCell className="max-w-0">
                <div className="flex items-center gap-2">
                    <span className="truncate">{config.name}</span>
                    <Badge variant="outline" className="font-normal text-xs shrink-0">
                        Current
                    </Badge>
                </div>
            </TableCell>
            <TableCell className="w-24 text-center">
                {config.published ? (
                    <Check className="size-4 text-green-600 mx-auto" />
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell className="w-20 text-center text-muted-foreground">—</TableCell>
            <TableCell className="w-16 text-center">
                <StatusIcon status={status} />
            </TableCell>
        </TableRow>
    );
}

/**
 * Row displaying a target config that can be selected
 *
 * @param {Object} config - The target config
 * @param {boolean} disabled - Whether the config is disabled
 * @param {boolean} isSelected - Whether the config is selected
 * @param {Array} matchingSlices - The matching slices for the config
 * @param {(config: Object) => void} onToggle - Callback to toggle the config selection
 * @param {string} status - The status of the config
 */
function ConfigRow({ config, disabled, isSelected, matchingSlices, onToggle, status }) {
    return (
        <TableRow
            className={cn('cursor-pointer', status === 'success' && 'bg-green-50', status === 'error' && 'bg-red-50')}
            onClick={() => !disabled && onToggle()}
        >
            <TableCell className="w-10">
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={onToggle}
                    disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                />
            </TableCell>
            <TableCell className="max-w-0">
                <Link
                    to={`/production/storefront-configurations/${config.id}`}
                    target="_blank"
                    className="truncate hover:underline inline-flex items-center gap-1 text-foreground!"
                    onClick={(e) => e.stopPropagation()}
                >
                    {config.name}
                    <ExternalLink className="size-3 shrink-0" />
                </Link>
            </TableCell>
            <TableCell className="w-24 text-center">
                {config.published ? (
                    <Check className="size-4 text-green-600 mx-auto" />
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell className="w-20 text-center">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="text-sm cursor-default">{matchingSlices.length}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[2001] max-w-xs">
                        <div className="gap-y-0.5">
                            {matchingSlices.map(({ index, slice }) => (
                                <div key={slice.id || index} className="text-sm">
                                    {slice.name}
                                </div>
                            ))}
                        </div>
                    </TooltipContent>
                </Tooltip>
            </TableCell>
            <TableCell className="w-16 text-center">
                <StatusIcon status={status} />
            </TableCell>
        </TableRow>
    );
}

/**
 * Status indicator icon (saving/success/error)
 *
 * @param {string} status - The status of the config
 */
function StatusIcon({ status }) {
    if (status === 'saving') {
        return <Loader2 className="size-4 animate-spin text-muted-foreground mx-auto" />;
    }
    if (status === 'success') {
        return <Check className="size-4 text-green-600 mx-auto" />;
    }
    if (status === 'error') {
        return <CircleAlert className="size-4 text-red-600 mx-auto" />;
    }
    return null;
}

export default ConfigurationsTable;
